import * as React from 'react';
import clsx from 'clsx';

import {Behavior, FunnelOrder, MetricType} from 'common/types/reports/bookmark';
import {react2panel} from 'common/util/react2panel';
import {withStyles} from 'common/util/react';
import {SvgIconReact, SvgIconType} from 'common/components/svg-icon';
import {rgbTripleFromHexString} from 'common/util/colors';
import {BehaviorTypeIcons, getBaseColorFromMetricType} from 'common/report/insights/util/metrics';
import {getEventDisplayName} from 'common/report/retention/util';
import {useTranslation} from 'common/i18n/hooks';
import {getAlphaLabelFromIndex} from 'common/util';

import styles from './index.module.styl';
import css from './index.module.styl?inline';
import funnelVisualTop from './funnel-visual-top.svg';
import funnelVisualBottom from './funnel-visual-bottom.svg';

export type Props = {
  behavior: Behavior;
  excludeBehaviorName?: boolean;
};

type DefinitionRow = {
  stepLabel?: string;
  stepIcon?: SvgIconType;
  name: string;
  conjunction?: string;
};

export const BehaviorDefinitionPanel = withStyles((props: Props) => {
  const i18n = useTranslation();

  const behaviorType = props.behavior.type!;
  const baseMetricColor = getBaseColorFromMetricType(behaviorType);

  let hasEventComparison = false;
  const subBehaviors = props.behavior.behaviors;
  const definitionRows = subBehaviors?.flatMap((subBehavior, subBehaviorIdx): DefinitionRow | DefinitionRow[] => {
    const isLastRow = subBehaviorIdx === subBehaviors.length - 1;
    if (behaviorType === MetricType.Simple) {
      return {
        name: getEventDisplayName(subBehavior.name!),
        conjunction: !isLastRow ? i18n.t(`components.behavior-definition-panel.conjunction.or`) : undefined,
      };
    } else if (behaviorType === MetricType.Funnel) {
      if (subBehavior.behaviors) {
        hasEventComparison = true;
        const eventComparisonBehaviors = subBehavior.behaviors;
        return eventComparisonBehaviors.map((eventComparisonBehavior, eventComparisonBehaviorIdx): DefinitionRow => {
          return {
            name: getEventDisplayName(eventComparisonBehavior.name!),
            stepLabel: `${subBehaviorIdx + 1}${getAlphaLabelFromIndex(eventComparisonBehaviorIdx)}`,
            conjunction:
              eventComparisonBehaviorIdx !== eventComparisonBehaviors.length - 1
                ? i18n.t(`components.behavior-definition-panel.conjunction.or`)
                : !isLastRow
                  ? i18n.t(`components.behavior-definition-panel.conjunction.then`)
                  : undefined,
          };
        });
      }
      return {
        name: getEventDisplayName(subBehavior.name!),
        ...(subBehavior.funnelOrder === FunnelOrder.Loose && {stepLabel: `${subBehaviorIdx + 1}`}),
        ...(subBehavior.funnelOrder === FunnelOrder.Any && {stepIcon: `any-order`}),
        conjunction: !isLastRow ? i18n.t(`components.behavior-definition-panel.conjunction.then`) : undefined,
      };
    }
    return {
      name: getEventDisplayName(subBehavior.name!),
      conjunction: !isLastRow ? i18n.t(`components.behavior-definition-panel.conjunction.then`) : undefined,
    };
  });

  return (
    <div
      className={clsx(styles.behaviorDefinitionPanel, `behavior-type-${behaviorType}`)}
      style={{
        '--behavior-base-color': baseMetricColor,
        '--behavior-base-color-rgb': rgbTripleFromHexString(baseMetricColor),
      }}
    >
      {!props.excludeBehaviorName && props.behavior.id && (
        <div className={styles.behaviorNameRow}>
          <SvgIconReact
            icon={BehaviorTypeIcons[behaviorType as keyof typeof BehaviorTypeIcons]}
            className={clsx(styles.behaviorIcon, {[styles.hasUnsavedChanges]: props.behavior.hasUnsavedChanges})}
          />
          <div className={styles.behaviorName}>{props.behavior.name}</div>
        </div>
      )}
      <div className={clsx(styles.behaviorDefinitionSection, {[styles.isSavedBehavior]: Boolean(props.behavior.id)})}>
        <div className={clsx(styles.behaviorVisual, `behavior-type-${behaviorType}`)}>
          {behaviorType === MetricType.Funnel && (
            <>
              <img className={styles.funnelVisualTop} src={funnelVisualTop} />
              <div className={styles.funnelVisualMiddle} />
              <img className={styles.funnelVisualBottom} src={funnelVisualBottom} />
            </>
          )}
        </div>
        <div className={clsx(styles.definitionRows, {[styles.hasEventComparison]: hasEventComparison})}>
          {definitionRows?.map((row, rowIdx) => {
            return (
              <div key={rowIdx} className={styles.definitionRow}>
                <SvgIconReact className={styles.behaviorIcon} icon='behavior-event' />
                {row.stepLabel ? (
                  <div className={styles.stepLabel}>{row.stepLabel}</div>
                ) : row.stepIcon ? (
                  <SvgIconReact className={styles.stepIcon} icon={row.stepIcon} />
                ) : undefined}
                <div className={styles.definitionRowName} title={row.name}>
                  {row.name}
                </div>
                {row.conjunction && <div className={styles.definitionRowConjunction}>{` ${row.conjunction}`}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}, css);

export const BehaviorDefinitionPanelEl = react2panel(`mp-behavior-definition-panel`, BehaviorDefinitionPanel);
